import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    EventClient,
    EventClientProvider,
    FormattedEvent,
    ShareOptionsProps,
    useEvents,
} from '@eb/event-renderer';
import { gettext } from '@eb/i18n';
import React from 'react';
import { GA_CATEGORY_EVENT_CARD, GA_LABEL_OPEN_MODAL } from '../../constants';
import {
    label,
    shareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AppProps } from '../../types';
import { getShareEventComponentProps } from '../ShareEvent/ShareEventRoot';
import { ShareEventModalComponentProps } from '../ShareEventModal/ShareEventModal';
import { dynaMount } from '../utils/mountComponent';
import { getOrganizerRelatedEventsFromDestination } from './api';

export interface MoreEventsFromThisOrganizerProps {
    isAuthenticated: boolean;
    organizerId: string;
    eventId: string;
    shareEventComponentProps: ShareEventModalComponentProps;
}

interface EventCardListProps {
    isAuthenticated: boolean;
    events: FormattedEvent[];
    shareEventComponentProps: ShareEventModalComponentProps;
}

export const MORE_EVENTS_FROM_THIS_ORGANIZER_ID =
    'listings-root__more-events-from-this-organizer';

const Heading = (): JSX.Element => (
    <h3 className="eds-text--center eds-text-hs eds-l-mar-bot-6">
        {gettext('More events from this organizer')}
    </h3>
);

const EventCardList = ({
    isAuthenticated,
    events,
    shareEventComponentProps,
}: EventCardListProps): JSX.Element => (
    <>
        {events?.map((event: FormattedEvent) => {
            const shareEventProps: ShareOptionsProps = {
                eventId: event.id,
                eventName: event.name,
                eventUrl: event.url,
                ...shareEventComponentProps,
            };

            return (
                <EventCard
                    {...CONSUMER_CARD_DEFAULTS}
                    eventData={event}
                    key={event.id}
                    gaCategory="related-events"
                    isAuthenticated={isAuthenticated}
                    type="list"
                    shareOptions={shareEventProps}
                    onDidShareClick={(id: shareType) =>
                        trackShareAction(label(id), GA_CATEGORY_EVENT_CARD)
                    }
                    onWillShareClick={() =>
                        trackShareAction(
                            GA_LABEL_OPEN_MODAL,
                            GA_CATEGORY_EVENT_CARD,
                        )
                    }
                />
            );
        })}
    </>
);

const eventClient = new EventClient();

export const MoreEventsFromThisOrganizer = (
    props: MoreEventsFromThisOrganizerProps,
): JSX.Element | null => {
    const {
        organizerId,
        eventId,
        isAuthenticated,
        shareEventComponentProps,
    } = props;
    const { data } = useEvents(
        'events',
        async () =>
            (await getOrganizerRelatedEventsFromDestination(
                organizerId,
                eventId,
            )) as any,
    );

    if (!data?.events) {
        return null;
    }

    return (
        <div className="eds-g-group">
            <div className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-12-12 eds-g-cell-mn-8-12 eds-g-offset-mn-2-12 eds-l-pad-top-8 eds-l-pad-hor-5">
                <Heading />
                <EventCardList
                    {...{
                        isAuthenticated,
                        events: data.events,
                        shareEventComponentProps,
                    }}
                />
            </div>
        </div>
    );
};

const MoreEventsFromThisOrganizerMount = (
    props: MoreEventsFromThisOrganizerProps,
): JSX.Element | React.ReactPortal =>
    dynaMount(
        {
            Component: MoreEventsFromThisOrganizer,
            containerId: MORE_EVENTS_FROM_THIS_ORGANIZER_ID,
        },
        props,
    );

const getMoreEventsFromThisOrganizerProps = (
    props: AppProps,
): MoreEventsFromThisOrganizerProps => {
    const {
        user: { isAuthenticated },
        organizer: { id: organizerId },
        event: { id: eventId },
    } = props;

    const shareEventModalProps = getShareEventComponentProps(props);

    return {
        isAuthenticated,
        organizerId,
        eventId,
        shareEventComponentProps: shareEventModalProps,
    };
};

const MoreEventsFromThisOrganizerRoot = (props: AppProps): JSX.Element => {
    const moreEventsFromThisOrganizerProps = getMoreEventsFromThisOrganizerProps(
        props,
    );

    return (
        <EventClientProvider client={eventClient}>
            <MoreEventsFromThisOrganizerMount
                {...moreEventsFromThisOrganizerProps}
            />
        </EventClientProvider>
    );
};

export default MoreEventsFromThisOrganizerRoot;
